import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';

import { bookingShape } from 'constants/propTypes';
import { BOOKING_WINDOW_MINUTES } from 'constants/booking';
import serviceTypeCodes from 'constants/serviceTypeCodes';
import CanPerform from 'components/shared/CanPerform';
import { FLOW_FORWARD } from 'constants/afterSalesShortcuts';
import { STD, SP, ER_STANDARD, STANDARD, COMFORT, PLUS } from 'constants/classOfService';
import ChildrenWillTravelAloneWarning from '../../AfterSalesWarning/ChildrenWillTravelAloneWarning/ChildrenWillTravelAloneWarning';
import { useHistory } from 'react-router-dom';
import SubsetOfPassengersInPalsBookingWarning from '../../AfterSalesWarning/SubsetOfPassengersInPalsBookingWarning/SubsetOfPassengersInPalsBookingWarning';

import {
  bookingIsConfirmed,
  runAfterSaleValidator,
  oneSegmentSelected,
  allSegmentsDepartToday,
  allSegmentsOfType,
  allSegmentsAreMinutesBeforeDeparture,
  allSegmentsOfService,
  allSelectedProductsConfirmed,
  passengersWithMixedJourneysInSegments,
  canceledPassengersInSegments,
  selectedItemsHaveLuggageExtras,
  getFaresForPassengersInMixedJourney,
  selectedProductsHaveDifferentProductFamily,
  getProductFamiliesPerPassengersInMixedPF
} from '../AfterSalesCtas.utils';
import { CtaButton } from '../AfterSalesCtas.styles';
import WarningCta from '../WarningCta/WarningCta';
import BlockTicketsWithExtraLuggageWarning from '../../AfterSalesWarning/BlockTicketsWithExtraLuggageWarning/BlockTicketsWithExtraLuggageWarning';
const FlowForwardCta = ({
  label,
  disabled,
  booking,
  booking: { reference },
  selectedItemRefs,
  willResultInChildAlone = false,
  exchangingSubsetOfPaxsInPALBooking = false
}) => {
  const history = useHistory();
  const [blockAftersalesAsChildrenWillTravelAlone, setBlockAftersalesAsChildrenWillTravelAlone] = useState(willResultInChildAlone);
  const [warningSubsetOfPaxsInPals, setWarningSubsetOfPaxsInPals] = useState(exchangingSubsetOfPaxsInPALBooking);
  const [showBlockTicketsWithExtraLuggageWarning, setShowBlockTicketsWithExtraLuggageWarning] = useState(false);

  const isDisabled =
    disabled ||
    !runAfterSaleValidator(
      booking,
      selectedItemRefs
    )([
      bookingIsConfirmed,
      oneSegmentSelected,
      allSelectedProductsConfirmed,
      allSegmentsDepartToday,
      allSegmentsOfType([STD, ER_STANDARD, STANDARD, SP, COMFORT, PLUS]),
      allSegmentsOfService([serviceTypeCodes.EUROSTAR]),
      allSegmentsAreMinutesBeforeDeparture(BOOKING_WINDOW_MINUTES)
    ]);

  const hasPassengersWithMixedJourneys = runAfterSaleValidator(
    booking,
    selectedItemRefs,
    null,
    'some'
  )([canceledPassengersInSegments, passengersWithMixedJourneysInSegments]);

  const faresForPassengersInMixedJourney = getFaresForPassengersInMixedJourney({
    booking,
    selectedItemRefs,
    hasPassengersWithMixedJourneys
  });

  const hasMoreThanOneProductFamily = runAfterSaleValidator(booking, selectedItemRefs)([selectedProductsHaveDifferentProductFamily]);

  const productFamiliesPerPassengersInMixedPF = getProductFamiliesPerPassengersInMixedPF({
    booking,
    selectedItemRefs,
    hasMoreThanOneProductFamily
  });

  const hasLuggageExtras = selectedItemsHaveLuggageExtras(booking, selectedItemRefs);

  if (blockAftersalesAsChildrenWillTravelAlone) {
    return <ChildrenWillTravelAloneWarning isOpen={true} closeModal={() => setBlockAftersalesAsChildrenWillTravelAlone(false)} />;
  }

  const toggleBlockTicketsWithExtraLuggageWarning = () => {
    if (hasLuggageExtras) {
      return setShowBlockTicketsWithExtraLuggageWarning(!showBlockTicketsWithExtraLuggageWarning);
    }

    const check =
      (willResultInChildAlone && setBlockAftersalesAsChildrenWillTravelAlone(true)) ||
      (exchangingSubsetOfPaxsInPALBooking && setWarningSubsetOfPaxsInPals(true));

    if (check) {
      return;
    }
  };

  return (
    <>
      <BlockTicketsWithExtraLuggageWarning
        isOpen={showBlockTicketsWithExtraLuggageWarning}
        closeModal={toggleBlockTicketsWithExtraLuggageWarning}
      />
      <CanPerform afterSalesShortcut={FLOW_FORWARD}>
        {(isEligible) =>
          isEligible && (hasPassengersWithMixedJourneys || hasMoreThanOneProductFamily) && !isDisabled ? (
            <WarningCta
              label={label}
              title={<FormattedMessage id="afterSales.warning" />}
              subtitle={<FormattedMessage id="afterSales.warningSelectionMessage" />}
              hasPassengersWithMixedJourneys={hasPassengersWithMixedJourneys}
              faresForPassengersInMixedJourney={faresForPassengersInMixedJourney}
              hasMoreThanOneProductFamily={hasMoreThanOneProductFamily}
              productFamiliesPerPassengersInMixedPF={productFamiliesPerPassengersInMixedPF}
            />
          ) : isEligible ? (
            warningSubsetOfPaxsInPals ? (
              <SubsetOfPassengersInPalsBookingWarning
                isOpen={true}
                closeModal={() => setWarningSubsetOfPaxsInPals(false)}
                handleClick={() =>
                  history.push(`/exchange/${reference}/flow-forward?itemRefs=${encodeURIComponent(selectedItemRefs)}&isFlowForward=true`)
                }
              />
            ) : (
              <CtaButton
                data-cy="flow-forward-cta"
                label={label}
                disabled={isDisabled}
                onClick={toggleBlockTicketsWithExtraLuggageWarning}
                // (willResultInChildAlone && setBlockAftersalesAsChildrenWillTravelAlone(true)) ||
                // (exchangingSubsetOfPaxsInPALBooking && setWarningSubsetOfPaxsInPals(true))

                element={
                  !isDisabled && !willResultInChildAlone && !exchangingSubsetOfPaxsInPALBooking && !hasLuggageExtras ? (
                    <Link to={`/exchange/${reference}/flow-forward?itemRefs=${encodeURIComponent(selectedItemRefs)}&isFlowForward=true`} />
                  ) : null
                }
              />
            )
          ) : null
        }
      </CanPerform>
    </>
  );
};

FlowForwardCta.propTypes = {
  label: PropTypes.node,
  disabled: PropTypes.bool,
  selectedItemRefs: PropTypes.arrayOf(PropTypes.string),
  booking: bookingShape,
  willResultInChildAlone: PropTypes.bool,
  exchangingSubsetOfPaxsInPALBooking: PropTypes.bool
};

export default FlowForwardCta;
